import { useCallback, useMemo, useState } from "react"

import { TextMatch } from "shared/text"
import { flatten } from "shared/util"

import { ResearchProjectResult, useListResearch } from "interface/research"

import { useSearch } from "features/search/context"
import { ResearcherSummary, ResearchYearStats } from "../types"

type useSisetusResearchPageProps = {
	sisetuCode: string
}
export const useSisetusResearchPage = ({
	sisetuCode
}: useSisetusResearchPageProps) => {
	const search = useSearch()
	
	const [searchWord, setSearchWord] = useState<string>('')
	const [selectedYear, setSelectedYear] = useState<number | null>(null)
	const [selectedResearcher, setSelectedResearcher] = useState<string | null>(null)

	const listState = useListResearch(
		search?.searchId,
		sisetuCode,
		{ updateTime: search?.updateTime }
	)

	const filterdProjectList = useMemo<ResearchProjectResult[]>(
		() => {
			if (!listState.data) return []
			if (searchWord !== '') {
				const match = new TextMatch(searchWord)
				return listState.data.filter(record => match.match(
  				record.projectName,
  				...record.researchs.flatMap(r => flatten([
						r.kubunName,
						r.bunyaName,
						r.jigyouName,
						r.projectName,
						r.abstract,
						r.purpose,
						r.method,
						r.results,
						r.conclusion
					]))
				))
			} else {
				return listState.data
			}
		},
		[listState.data, searchWord]
	)

	const isFiltered = useCallback(
		(project: ResearchProjectResult, condition?: {
			selectedYear?: number | null,
			selectedResearcher?: string | null
		}) => {
			if (condition?.selectedYear) {
				if (project.startNendo > condition.selectedYear || project.endNendo < condition.selectedYear) {
					return true
				}
			}
			if (condition?.selectedResearcher) {
				if (!project.researchers.some(kojinCode => kojinCode === condition.selectedResearcher)) return true
			}
			return false
		},
		[]
	)

	const yearsStats = useMemo<ResearchYearStats>(
		() => {
			const list: { year: number, count: number, cost: number }[] = []
			let startYear: number | null = null
			let lastYear: number | null = null
			let maxCount = 0
			let maxCost = 0
			filterdProjectList
				.filter(project => isFiltered(project, { selectedResearcher }) === false)
				.forEach(project => {
					if (!startYear || startYear > project.startNendo) startYear = project.startNendo
					if (!lastYear || lastYear < project.endNendo) lastYear = project.endNendo

					for(let year = project.startNendo; year <= project.endNendo; year++) {
						const research = project.researchs.find(r => r.nendo === year)
						const cost = research?.researchCost ?? 0
						const exist = list.find(exist => exist.year === year)
						if (exist) {
							exist.count++
							exist.cost += cost
							maxCount = Math.max(maxCount, exist.count)
							maxCost = Math.max(maxCost, exist.cost)
						} else {
							list.push({ year, count: 1, cost })
							maxCount = Math.max(maxCount, 1)
							maxCost = Math.max(maxCost, cost)
						}
					}
				})
			list.sort((a, b) => b.year - a.year)

			const years: ResearchYearStats["list"] = []
			if (startYear && lastYear) {
				for(let year = startYear; year <= lastYear; year++) {
					const e = list.find(r => r.year === year)
					years.push({ year, count: e?.count ?? null, cost: e?.cost ?? null })
				}
			}
			return {
				list: years,
				maxCount,
				maxCost
			}
		},
		[filterdProjectList, selectedResearcher, isFiltered]
	)
	const projects = useMemo<ResearchProjectResult[]>(
		() => filterdProjectList.filter(
			trial => isFiltered(trial, { selectedYear, selectedResearcher }) === false
		),
		[filterdProjectList, isFiltered, selectedYear, selectedResearcher]
	)
	const researchers = useMemo<ResearcherSummary[]>(
		() => {
			const researcherMap: {
				[kojinCode: string]: ResearcherSummary
			} = {}
			filterdProjectList
				.filter(project => isFiltered(project, { selectedYear }) === false)
				.forEach(project => project.researchs.forEach(research => {
					if (research.primaryResearcher) {
						const researcher = research.primaryResearcher
						const key = researcher.kojinCode ?? researcher.kojinName
						const exist = researcherMap[key]
						if (exist) {
							exist.researchCount++
							exist.primaryResearchCount++
							exist.primaryCost += research.researchCost ?? 0
							if (!exist.projects.includes(project.projectKey)) {
								exist.projects.push(project.projectKey)
								exist.primaryProjectCount++
							}
						} else {
							researcherMap[key] = {
								...researcher,
								researchCount: 1,
								primaryResearchCount: 1,
								supportResearchCount: 0,
								projects: [project.projectKey],
								primaryProjectCount: 1,
								supportProjectCount: 0,
								primaryCost: research.researchCost ?? 0,
								supportCost: 0
							}
						}
					}
					research.supportResearchers.map(researcher => {
						const key = researcher.kojinCode ?? researcher.kojinName
						const exist = researcherMap[key]
						if (exist) {
							exist.researchCount++
							exist.supportResearchCount++
							exist.supportCost += research.researchCost ?? 0
							if (!exist.projects.includes(project.projectKey)) {
								exist.projects.push(project.projectKey)
								exist.supportProjectCount++
							}
						} else {
							researcherMap[key] = {
								...researcher,
								researchCount: 1,
								primaryResearchCount: 0,
								supportResearchCount: 1,
								projects: [project.projectKey],
								primaryProjectCount: 0,
								supportProjectCount: 1,
								primaryCost: 0,
								supportCost: research.researchCost ?? 0
							}
						}
					})
				}))
			const researchers = Object.values(researcherMap)
			researchers.sort((a, b) => {
				if (b.researchCount === a.researchCount) {
					if (b.primaryResearchCount === a.primaryResearchCount) {
						const costA = a.primaryCost + a.supportCost
						const costB = b.primaryCost + b.supportCost
						if (costA === costB) {
							return a.kojinName.localeCompare(b.kojinName)
						}
						return costB - costA
					}
					return b.primaryResearchCount - a.primaryResearchCount
				}
				return b.researchCount - a.researchCount
			})
			return researchers
		},
		[filterdProjectList, isFiltered, selectedYear]
	)
	return {
		searchWord, setSearchWord,
		selectedYear, setSelectedYear,
		selectedResearcher, setSelectedResearcher,
		loading: listState.loading,
		error: listState.error,
		projects, yearsStats, researchers
	}
}